import { EnhancedTable } from "@mb-pro-ui/components/table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Column } from "react-table";
import { useErrorHandler } from "../hooks/useErrorHandler";
import { useMutation } from "react-query";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Tooltip, Typography } from "@mui/material";
import timezones from "timezones-list";

import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";

import { useDevice } from "../utils/DeviceProvider";
import { useApi } from "../utils/ApiProvider";
import { EmailNotification, NotificationEmailsResponse } from "../utils/types";
import { useLocale } from "../locales/LocaleProvider";
import { Form, useField } from "react-final-form";
import { Autocomplete, Select, TextField } from "@mb-pro-ui/components/form";

export const EmailNotificationSubpage = () => {
  const [open, setOpen] = useState(false);
  const handleDialogOpen = () => setOpen(true);
  const handleDialogClose = () => setOpen(false);

  const [emailToDelete, setEmailToDelete] = useState<string | null>(null);
  const handleEmailToDeleteClose = () => setEmailToDelete(null);

  const {
    device: { unitid },
  } = useDevice();
  const { formatMessage } = useIntl();

  const { errorHandler: onError } = useErrorHandler([[{ status: 400, type: "email" }, () => formatMessage({defaultMessage: "E-mail address already added."})], [{ status: 400 }, () => null]]);
  const api = useApi();

  const getEmailsQuery = useCallback(() => api<NotificationEmailsResponse>(`/units/${unitid}/email`), [api, unitid]);
  const { mutate: getEmails, data: emails, status: queryStatus } = useMutation({ mutationFn: getEmailsQuery, onError });

  const addEmailQuery = useCallback(
    (value: EmailNotification) =>
      api(`/units/${unitid}/email`, { method: "post", headers: { "Content-Type": "application/json" }, body: JSON.stringify(value) }),
    [api, unitid]
  );
  const { mutate: addEmail, isLoading: addIsLoading } = useMutation({ mutationFn: addEmailQuery, onError, onSuccess: () => getEmails() });

  const deleteEmailQuery = useCallback(
    (email: string) => api(`/units/${unitid}/email`, { method: "delete", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }) }),
    [api, unitid]
  );
  const { mutate: deleteEmailMutate, isLoading: deleteIsLoading } = useMutation({ mutationFn: deleteEmailQuery, onError, onSuccess: () => getEmails() });
  const deleteEmail = useCallback(() => {
    if (!emailToDelete) {
      return;
    }
    deleteEmailMutate(emailToDelete);
    setEmailToDelete(null);
  }, [deleteEmailMutate, emailToDelete]);

  const isLoading = useMemo(() => addIsLoading || deleteIsLoading, [addIsLoading, deleteIsLoading]);

  const columns = useMemo(
    (): Column<EmailNotification>[] => [
      {
        id: "email",
        Header: formatMessage({
          defaultMessage: "E-mail",
        }),
        accessor: (item) => item.email,
        width: 100,
      },
      {
        id: "locale",
        Header: formatMessage({
          defaultMessage: "Locale",
        }),
        accessor: (item) => item.locale,
        width: 100,
      },
      {
        id: "timezone",
        Header: formatMessage({
          defaultMessage: "Time zone",
        }),
        accessor: (item) => item.timezone,
        width: 100,
      },
    ],
    [formatMessage]
  );

  useEffect(() => {
    getEmails();
  }, [getEmails]);

  return (
    <>
      <EnhancedTable
        columns={columns}
        data={emails ?? []}
        showResultCount
        queryStatus={queryStatus}
        overscanCount={5}
        disableGlobalFilter
        actionPosition="right"
        actionCellWidth={50}
        action={({ original }) => (
          <IconButton size="small" onClick={() => setEmailToDelete(original.email)} disabled={isLoading}>
            <ClearIcon />
          </IconButton>
        )}
        prefix={
          <Typography variant="subtitle2" sx={{ color: (theme) => theme.palette.primary.contrastText }}>
            {formatMessage({ defaultMessage: "E-mail notification" })}
          </Typography>
        }
        postfix={
          <>
            <IconButton
              size="small"
              sx={{
                color: "primary.contrastText",
              }}
              onClick={handleDialogOpen}
              disabled={isLoading}
            >
              <Tooltip
                title={formatMessage({
                  defaultMessage: "Add new e-mail",
                })}
              >
                <AddIcon />
              </Tooltip>
            </IconButton>
          </>
        }
      />
      <Dialog open={open} onClose={handleDialogClose}>
        <EmailNotificationForm handleDialogClose={handleDialogClose} addEmail={addEmail} />
      </Dialog>
      <Dialog
        open={emailToDelete !== null}
        onClose={handleEmailToDeleteClose}
        PaperProps={{
          sx: {
            minWidth: { xs: 9 / 10, sm: 400 },
            width: { xs: 9 / 10, sm: 400 },
          },
        }}
      >
        <DialogTitle sx={{ backgroundColor: (theme) => theme.palette.error.main, color: (theme) => theme.palette.error.contrastText }}>
          {formatMessage({ defaultMessage: "Delete e-mail address" })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{emailToDelete}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEmailToDeleteClose} color="inherit">
            {formatMessage({ defaultMessage: "Cancel" })}
          </Button>
          <Button onClick={deleteEmail} color="error">
            {formatMessage({ defaultMessage: "Delete" })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const TimestampCheckLabel = () => {
  const {
    input: { value: locale },
  } = useField("locale");
  const {
    input: { value: timezone },
  } = useField("timezone");
  const {
    input: { value: tzCountry },
  } = useField("tz-country");

  const api = useApi();
  const { formatMessage } = useIntl();

  const labelQuery = () =>
    locale & timezone
      ? Promise.resolve({ formatted: "" })
      : api<{ formatted: string }>(`/timestamp-format-check?locale=${[locale, tzCountry].filter((i) => !!i).join("-")}&timezone=${timezone}`);
  const { mutate: checkFormat, data } = useMutation({ mutationFn: labelQuery });

  useEffect(() => {
    checkFormat();
  }, [checkFormat, locale, tzCountry, timezone]);

  return (
    <>
      <Typography variant="caption">{formatMessage({ defaultMessage: "Timestamp format displayed in email" })}</Typography>
      <Typography>{data?.formatted}</Typography>
    </>
  );
};

type EmailForm = { email: string; locale: string; timezone: string; ["tz-country"]?: string };
const EmailNotificationForm = ({ handleDialogClose, addEmail }: { handleDialogClose: () => void; addEmail: (value: EmailNotification) => void }) => {
  const { formatMessage } = useIntl();
  const { locale, locales } = useLocale();
  const browserTimezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  const onSubmit = useCallback(
    (value: EmailForm) => {
      const { email, locale, timezone, "tz-country": tzCountry } = value;
      addEmail({ email, locale: [locale, tzCountry].filter((i) => !!i).join("-"), timezone });
      handleDialogClose();
    },
    [addEmail, handleDialogClose]
  );

  return (
    <Form<EmailForm>
      onSubmit={onSubmit}
      render={({ handleSubmit }) => (
        <form onSubmit={handleSubmit}>
          <DialogTitle>{formatMessage({ defaultMessage: "Add new e-mail" })}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              required
              // margin="dense"
              id="email"
              name="email"
              label={formatMessage({ defaultMessage: "E-mail address" })}
              type="email"
              fullWidth
              variant="standard"
              highlightDirty={false}
            />
            <Select
              name="locale"
              defaultValue={locale}
              required
              label={formatMessage({ defaultMessage: "Locale" })}
              variant="standard"
              // margin="dense"
              fullWidth
              // outerSx={{ m: 0 }}
              items={locales.map((l) => ({ value: l }))}
              renderOption={({ option }) => option.value}
              highlightDirty={false}
            />
            <Autocomplete
              name="timezone"
              defaultValue={browserTimezone}
              size="small"
              required
              label={formatMessage({ defaultMessage: "Time zone" })}
              options={timezones.map(({ tzCode }) => tzCode)}
              fullWidth
              variant="standard"
              freeSolo
              // sx={{ ".MuiFormControl-root": { m: 0 } }}
              highlightDirty={false}
            />
            <TextField
              // margin="dense"
              id="tz-country"
              name="tz-country"
              label={formatMessage({ defaultMessage: "Time zone country code" })}
              fullWidth
              variant="standard"
              highlightDirty={false}
            />
            <TimestampCheckLabel />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} color="inherit">
              {formatMessage({ defaultMessage: "Cancel" })}
            </Button>
            <Button type="submit"> {formatMessage({ defaultMessage: "Add" })}</Button>
          </DialogActions>
        </form>
      )}
    />
  );
};
